import React, {
  MouseEvent,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import { NavLink } from 'react-router-dom';

import type { LucideIcon } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Size, getIconPixels } from '@/theme/size-tokens';

export type LeftNavItemProps = {
  /** Lucide icon component to display */
  icon?: LucideIcon;
  /** Icon size from unified size system */
  iconSize?: Size;
  /** Link label text (visible when expanded, hidden when collapsed) */
  children: ReactNode;
  /** Whether this item is currently active (highlighted state) */
  active?: boolean;
  /** Additional CSS classes applied to the navigation link */
  className?: string;
  /** Badge or count component to display on the right side */
  badge?: ReactNode;
  /** Destination URL for the navigation link (required for link mode) */
  href?: string;
  /** Accessible label override (used for tooltip in collapsed mode) */
  title?: string;
  /** Prevent actual navigation for demo/showcase mode (deprecated: use onClick instead) */
  preventNavigation?: boolean;
  /** Click handler - when provided, renders as button instead of link */
  onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Disable active state highlighting (useful for non-navigation items like header buttons) */
  disableActiveState?: boolean;
};

/**
 * LeftNavItem - Navigation link or button item for LeftNavLayout
 *
 * Features:
 * - Polymorphic: renders as link (with href) or button (with onClick)
 * - Icon support with proper alignment
 * - Optional active state styling
 * - Tooltip on hover in collapsed mode (using Tooltip component with Portal)
 * - Badge/count support
 * - Accessible keyboard navigation
 *
 * @example
 * ```tsx
 * // Link mode
 * <LeftNavItem icon={Home} href="/" active>
 *   Home
 * </LeftNavItem>
 *
 * // Button mode
 * <LeftNavItem icon={Settings} onClick={() => console.log('clicked')} disableActiveState>
 *   Settings
 * </LeftNavItem>
 * ```
 */
function LeftNavItem({
  icon: IconComp,
  iconSize = 'sm',
  children,
  active = false,
  className = '',
  badge,
  href,
  title,
  preventNavigation = false,
  onClick,
  disableActiveState = false,
}: Readonly<LeftNavItemProps>) {
  const navRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Try to get initial collapsed state from nav element if it exists
    if (typeof window !== 'undefined') {
      const navElement = document.querySelector('nav[data-collapsed]');
      return navElement?.getAttribute('data-collapsed') === 'true';
    }
    return false;
  });

  // Check if nav is collapsed by looking at parent nav element
  useEffect(() => {
    const checkCollapsed = () => {
      const navElement = navRef.current?.closest('nav');
      if (navElement) {
        setIsCollapsed(navElement.getAttribute('data-collapsed') === 'true');
      }
    };

    checkCollapsed();

    // Create observer to watch for attribute changes
    const navElement = navRef.current?.closest('nav');
    if (navElement) {
      const observer = new MutationObserver(checkCollapsed);
      observer.observe(navElement, {
        attributes: true,
        attributeFilter: ['data-collapsed'],
      });
      return () => observer.disconnect();
    }

    return undefined;
  }, []);

  // Use children as fallback title for accessibility
  const itemTitle =
    title ?? (typeof children === 'string' ? children : undefined);

  // Determine mode: button (onClick present) or link (href present)
  const isButtonMode = !!onClick;
  const isLinkMode = !isButtonMode && !!href;

  // Shared base classes for both button and link modes
  const getBaseClasses = (isActive: boolean) =>
    `group relative flex items-center gap-1 px-2 py-1 rounded-lg mx-2 [nav[data-pre-collapsed='true']_&]:gap-0 [nav[data-pre-collapsed='true']_&]:justify-center text-sm font-medium transition-all duration-200 ${
      !disableActiveState && (isActive || active)
        ? 'bg-sidebar-primary/50 text-sidebar-primary-foreground shadow-sm'
        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
    } ${className}`;

  // Shared content renderer
  const renderContent = (isActive: boolean) => (
    <>
      {IconComp && <IconComp size={getIconPixels(iconSize, 'desktop')} />}

      <span className="flex-1 truncate overflow-hidden whitespace-nowrap transition-[opacity,max-width,flex] duration-200 [nav[data-pre-collapsed='true']_&]:max-w-0 [nav[data-pre-collapsed='true']_&]:flex-none [nav[data-pre-collapsed='true']_&]:opacity-0">
        {children}
      </span>

      {badge && (
        <span className="shrink-0 overflow-hidden transition-[opacity,max-width] duration-200 [nav[data-pre-collapsed='true']_&]:max-w-0 [nav[data-pre-collapsed='true']_&]:opacity-0">
          {/* Auto-inject inverted prop when nav item is active */}
          {isValidElement(badge) && !disableActiveState && (isActive || active)
            ? cloneElement(badge, { inverted: true } as { inverted: boolean })
            : badge}
        </span>
      )}
    </>
  );

  // Button mode rendering
  const buttonElement = isButtonMode ? (
    <a
      ref={navRef as React.RefObject<HTMLAnchorElement>}
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e as unknown as MouseEvent<HTMLAnchorElement>);
      }}
      className={getBaseClasses(active)}
      aria-current={!disableActiveState && active ? 'page' : undefined}
      aria-label={itemTitle}
      role="button"
    >
      {renderContent(active)}
    </a>
  ) : null;

  // Link mode rendering
  const navLink = isLinkMode ? (
    <NavLink
      ref={navRef as React.RefObject<HTMLAnchorElement>}
      to={href!}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        if (preventNavigation) {
          e.preventDefault();
        }
      }}
      className={({ isActive }) => getBaseClasses(isActive)}
      aria-current={!disableActiveState && active ? 'page' : undefined}
      aria-label={itemTitle}
      end
    >
      {({ isActive }) => renderContent(isActive)}
    </NavLink>
  ) : null;

  const element = isButtonMode ? buttonElement : navLink;

  // Only show tooltip when collapsed
  if (!isCollapsed) {
    return element;
  }

  // Get tooltip content
  const tooltipContent =
    typeof children === 'string' ? children : (itemTitle ?? 'Menu Item');

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span className="block w-full">{element}</span>
        </TooltipTrigger>
        <TooltipContent side="right">{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

LeftNavItem.displayName = 'LeftNavItem';

export default LeftNavItem;
